import type { BrokerCapabilities, BrokerCsvImportCapabilities } from "../services/brokerIntegrationApi";
import { formatAssetClassLabel, isTradedAssetClassesField } from "./tradingViewBroker";

/** Default universe when capabilities omit explicit lists (matches API schema). */
export const DEFAULT_TRADED_ASSET_CLASSES = [
  "commodity",
  "crypto",
  "energy",
  "equity",
  "fx",
  "futures",
  "index",
  "metal",
] as const;

export type TradedAssetClassesContext =
  | "connect_credentials"
  | "broker_sync"
  | "oauth_connect"
  | "instrument_setup"
  | string;

export interface BrokerTradedAssetClassesConfig {
  required: boolean;
  userSelectable: boolean;
  allowedAssetClasses: string[];
  fixedValues: string[] | null;
  multi: boolean;
  uiControl: string;
  contexts: TradedAssetClassesContext[];
  fieldLabels: Record<string, string>;
}

function parseStringArray(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((v) => String(v).trim().toLowerCase()).filter(Boolean);
}

function hasContext(config: BrokerTradedAssetClassesConfig, context: TradedAssetClassesContext): boolean {
  return config.contexts.some((c) => String(c).toLowerCase() === context.toLowerCase());
}

function parseTradedAssetClassesBlock(
  raw: unknown,
  schemaAllowed: string[]
): BrokerTradedAssetClassesConfig | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const allowed = parseStringArray(o.allowed_asset_classes);
  const fixed = parseStringArray(o.fixed_values);
  const contexts = parseStringArray(o.contexts) as TradedAssetClassesContext[];
  const uiControl = String(o.ui_control || o.uiControl || "").trim().toLowerCase();
  const multi =
    o.multi === true ||
    uiControl === "multi_select" ||
    (uiControl !== "single_select" && fixed.length !== 1);

  return {
    required: o.required === true,
    userSelectable: o.user_selectable !== false && fixed.length === 0,
    allowedAssetClasses:
      allowed.length > 0
        ? allowed
        : fixed.length > 0
          ? fixed
          : schemaAllowed.length > 0
            ? schemaAllowed
            : [...DEFAULT_TRADED_ASSET_CLASSES],
    fixedValues: fixed.length > 0 ? fixed : null,
    multi,
    uiControl: uiControl || (multi ? "multi_select" : "single_select"),
    contexts,
    fieldLabels:
      o.field_labels && typeof o.field_labels === "object"
        ? (o.field_labels as Record<string, string>)
        : {},
  };
}

function resolveSchemaAllowed(cap: BrokerCapabilities | null | undefined): string[] {
  const schema = cap?.traded_asset_classes_schema;
  if (!schema || typeof schema !== "object") return [];
  return parseStringArray((schema as Record<string, unknown>).allowed_asset_classes);
}

/** Top-level `traded_asset_classes` from GET /broker/capabilities. */
export function resolveBrokerTradedAssetClasses(
  cap: BrokerCapabilities | null | undefined
): BrokerTradedAssetClassesConfig | null {
  if (!cap) return null;
  const schemaAllowed = resolveSchemaAllowed(cap);
  return parseTradedAssetClassesBlock(cap.traded_asset_classes, schemaAllowed);
}

export function tradedAssetClassesFieldLabel(
  config: BrokerTradedAssetClassesConfig | null | undefined,
  fallback = "Asset classes"
): string {
  const fromLabels = config?.fieldLabels?.traded_asset_classes;
  if (typeof fromLabels === "string" && fromLabels.trim()) return fromLabels.trim();
  return fallback;
}

/**
 * Connect / OAuth forms — never require a user multi-select at connect time.
 * BE treats `traded_asset_classes` as optional (stores `[]` when omitted).
 * Only surface fixed values (e.g. Hyperliquid crypto) as read-only.
 */
export function resolveConnectTradedAssetClasses(
  cap: BrokerCapabilities | null | undefined
): BrokerTradedAssetClassesConfig | null {
  const spec = resolveBrokerTradedAssetClasses(cap);
  if (!spec) return null;
  if (
    !hasContext(spec, "connect_credentials") &&
    !hasContext(spec, "oauth_connect")
  ) {
    return null;
  }
  if (spec.userSelectable) return null;
  if ((spec.fixedValues?.length ?? 0) === 0) return null;
  return { ...spec, required: false };
}

/** Instrument setup wizard — filter asset-class picker via `allowed_asset_classes`. */
export function resolveInstrumentSetupTradedAssetClasses(
  cap: BrokerCapabilities | null | undefined
): BrokerTradedAssetClassesConfig | null {
  const spec = resolveBrokerTradedAssetClasses(cap);
  if (!spec || !hasContext(spec, "instrument_setup")) return null;
  return spec;
}

/** CSV / xlsx / html import lane — optional unless fixed values (crypto / futures). */
export function resolveCsvTradedAssetClasses(
  cap: BrokerCapabilities | null | undefined,
  csvImport: {
    allowedAssetClasses?: string[];
    fixedAssetClasses?: string[];
  } | null | undefined
): BrokerTradedAssetClassesConfig | null {
  const spec = resolveBrokerTradedAssetClasses(cap);
  if (spec && hasContext(spec, "csv_import")) {
    if (spec.userSelectable) return null;
    if ((spec.fixedValues?.length ?? 0) > 0) {
      return { ...spec, required: false };
    }
    return null;
  }
  const fixedFromCsv = parseStringArray(csvImport?.fixedAssetClasses);
  if (fixedFromCsv.length > 0) {
    return {
      required: false,
      userSelectable: false,
      allowedAssetClasses: fixedFromCsv,
      fixedValues: fixedFromCsv,
      multi: fixedFromCsv.length > 1,
      uiControl: fixedFromCsv.length > 1 ? "multi_select" : "single_select",
      contexts: ["csv_import"],
      fieldLabels: {},
    };
  }
  return null;
}

export function parseSavedTradedAssetClasses(
  credentials: Record<string, unknown> | null | undefined
): string[] {
  if (!credentials) return [];
  const raw =
    credentials.traded_asset_classes ??
    credentials.tradedAssetClasses ??
    credentials.asset_classes;
  if (Array.isArray(raw)) {
    return raw.map((v) => String(v).trim().toLowerCase()).filter(Boolean);
  }
  if (typeof raw === "string" && raw.trim()) {
    return raw
      .split(/[,;]+/)
      .map((v) => v.trim().toLowerCase())
      .filter(Boolean);
  }
  return [];
}

export function normalizeTradedAssetClassesForApi(classes: string[]): string[] {
  return classes.map((c) => c.trim().toLowerCase()).filter(Boolean);
}

export function formatTradedAssetClassesReadOnly(classes: string[]): string {
  return classes.map((c) => formatAssetClassLabel(c)).join(", ");
}

export function shouldRenderConnectAssetClassPicker(
  config: BrokerTradedAssetClassesConfig | null | undefined
): boolean {
  return !!config?.userSelectable && (config.allowedAssetClasses.length > 0 || config.required);
}

export function shouldRenderConnectAssetClassReadOnly(
  config: BrokerTradedAssetClassesConfig | null | undefined
): boolean {
  return !!config && !config.userSelectable && (config.fixedValues?.length ?? 0) > 0;
}

export function mergeTradedAssetClassesIntoPayload(
  payload: Record<string, unknown>,
  connectAssetClasses: string[],
  config: BrokerTradedAssetClassesConfig | null | undefined
): Record<string, unknown> {
  const { traded_asset_classes: _a, tradedAssetClasses: _b, ...rest } = payload;
  if (!config) return rest;
  const classes = config.userSelectable
    ? normalizeTradedAssetClassesForApi(connectAssetClasses)
    : normalizeTradedAssetClassesForApi(config.fixedValues ?? []);
  if (classes.length === 0 && !config.required) return rest;
  return { ...rest, traded_asset_classes: classes };
}

/**
 * Query params for generic statement import (Binance, OANDA, …) — merges picker /
 * fixed `traded_asset_classes` into the CSV upload request (required by the API).
 */
export function buildGenericBrokerCsvImportParams(
  values: Record<string, string>,
  assetClasses: string[],
  config: BrokerTradedAssetClassesConfig | null | undefined
): Record<string, string | string[]> {
  let classes = assetClasses;
  if (classes.length === 0) {
    const single = (values.traded_asset_classes || values.tradedAssetClasses || "").trim();
    if (single) classes = [single];
  }
  const normalized = normalizeTradedAssetClassesForApi(classes);
  const merged = mergeTradedAssetClassesIntoPayload(values, normalized, config) as Record<
    string,
    string | string[]
  >;
  if (normalized.length > 0 && merged.traded_asset_classes == null) {
    merged.traded_asset_classes = normalized;
  }
  return merged;
}

/** Legacy csv_import-only shape (nested under import capabilities). */
export function tradedAssetClassesFromCsvCapabilities(
  csv?: BrokerCsvImportCapabilities | null
): Pick<BrokerTradedAssetClassesConfig, "allowedAssetClasses" | "fixedValues" | "multi"> | null {
  if (!csv) return null;
  const allowed = parseStringArray(csv.allowed_asset_classes);
  const fixed = parseStringArray(csv.fixed_traded_asset_classes);
  if (!allowed.length && !fixed.length) return null;
  return {
    allowedAssetClasses: allowed.length ? allowed : fixed,
    fixedValues: fixed.length ? fixed : null,
    multi: fixed.length !== 1,
  };
}
